
/* eslint-disable no-mixed-operators */
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import { styled } from "@mui/system";
import React, { useState } from "react";
import { branchesReleaseNamesSelector } from "src/stores/selectors/branchDeployerSelector";
import { DIALOG_TYPES, FIELD_TYPES, NUMERIC_FIELD_CONSTRAINTS } from "./BranchDeployer.consts";
import useBranchDeployerDialog from "./useBranchDeployerDialog";
import useAppStore from "../../stores/appStore";
import HyroMultiSelect from "@hyro-components/fields/multi-select/HyroMultiSelect";
import CustomTooltip from "@hyro-components/Tooltip";
import QuestionIcon from "src/icons/insights/QuestionIcon";
import { useFetchDashboardSettings } from "src/queries/hooks/settings";
import { HyroButton } from "@hyro-components";
import { BUTTON_COLORS, BUTTON_VARIANTS } from "@hyro-components/hyro-button/HyroButton.consts";
import { BRANCH_DEPLOYER_STORAGE_KEY, KEYS_TO_FILTER, KEYS_TO_FILTER_WITH_CONFIG_ID } from "../../components/branch-deployer/BranchDeployer.consts";
import { filterObjectByKeys, isValidObjectId } from "@utils";
import { getFlag } from "src/utils/helpers/ldClient";

const ERROR_MESSAGES = {
  DEDICATED_MODE_REQUIRED: "'Dedicated' NLU Runtime Mode is required when deploying with config ID or assistant ID",
  API_MODE_REQUIRES_RUNTIME: "NLU Runtime parameter is required when deploying API mode",
  INVALID_RELEASE_NAME: "Release name can contain lowercase letters, digits, hyphens, and dots, and must start with a letter and end with a letter or digit.",
  RELEASE_NAME_TOO_LONG: "Release name must not exceed 33 characters.",
  RELEASE_NAME_EXISTS: "Release name already exists.",
  AGENT_REQUIRED_WITH_CONFIG: "When you add a config ID, you must also add an assistant ID.",
  CONFIG_REQUIRED_WITH_AGENT: "When you add an agent ID, you must also add a config ID.",
  INVALID_NUMBER: "Please enter a valid number.",
  MEMORY_REQUIRED: "Memory must be between 1 and 10.",
  INVALID_CONFIG_ID: "Config ID must be a valid ObjectId (24 hex characters), 'active', or 'draft'."
};

const BranchDeployerDialog = ({
  open,
  type,
  data,
  onClose,
  onSubmit
}) => {
  const branchesReleaseNames = useAppStore(branchesReleaseNamesSelector);
  const { dialogContent } = useBranchDeployerDialog({
    type, data
  });
  const dashboardSettings = useFetchDashboardSettings()?.data;
  const [bdData, setBdData] = useState(data);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const formatAndValidateString = (input) => {
    const formattedString = input.replace(/ /g, "-");
    const isValid = /^[a-z]([-a-z0-9]*[a-z0-9])?(\.[a-z0-9]([-a-z0-9]*[a-z0-9])?)*$/.test(formattedString);

    return {
      formattedString,
      isValid
    };
  };

  const validations = {
    [FIELD_TYPES.RELEASE_NAME]: (value) => {
      const { formattedString, isValid } = formatAndValidateString(value);
      if (!isValid) {
        return ERROR_MESSAGES.INVALID_RELEASE_NAME;
      }

      if (formattedString.length > 32) {
        return ERROR_MESSAGES.RELEASE_NAME_TOO_LONG;
      }

      if (branchesReleaseNames.includes(formattedString)) {
        return ERROR_MESSAGES.RELEASE_NAME_EXISTS;
      }
      return "";
    },
    [FIELD_TYPES.BOT]: (value, bdData) => "",
    [FIELD_TYPES.CONFIG_ID]: (value, bdData) => {
      if (!value || (typeof value === "string" && value.trim() === "")) {
        return "";
      }
      const trimmedValue = typeof value === "string" ? value.trim() : String(value);
      if (trimmedValue === "active" || trimmedValue === "draft" || isValidObjectId(trimmedValue)) {
        return "";
      }
      return ERROR_MESSAGES.INVALID_CONFIG_ID;
    },
    [FIELD_TYPES.ASSISTANT_ID]: (value, bdData) => "",
    [FIELD_TYPES.NLU_RUNTIME_MODES]: (value, bdData) => ((bdData[FIELD_TYPES.CONFIG_ID] || bdData[FIELD_TYPES.ASSISTANT_ID]) && Array.isArray(value) && !value.includes("dedicated") && import.meta.env.VITE_ENV !== "production") ? ERROR_MESSAGES.DEDICATED_MODE_REQUIRED : "",
    [FIELD_TYPES.NLU_RUNTIME]: (value, bdData) => (Array.isArray(bdData[FIELD_TYPES.NLU_RUNTIME_MODES]) && bdData[FIELD_TYPES.NLU_RUNTIME_MODES].includes("api") && (!value || value.trim() === "")) ? ERROR_MESSAGES.API_MODE_REQUIRES_RUNTIME : "",
    [FIELD_TYPES.MEMORY]: (value) => (value && (typeof value === "string" ? value.trim() !== "" : true)) ? (isNaN(Number(value)) || Number(value) < NUMERIC_FIELD_CONSTRAINTS.MEMORY.min || Number(value) > NUMERIC_FIELD_CONSTRAINTS.MEMORY.max) ? ERROR_MESSAGES.MEMORY_REQUIRED : "" : ""
  };

  function validateField(name, value, newBdData) {
    const error = validations[name] ? validations[name](value, newBdData) : "";
    return error;
  }

  const updateRelatedErrors = (name, newBdData, newErrors) => {
    if (name === FIELD_TYPES.NLU_RUNTIME_MODES) {
      newErrors[FIELD_TYPES.NLU_RUNTIME] = validateField(FIELD_TYPES.NLU_RUNTIME, newBdData[FIELD_TYPES.NLU_RUNTIME], newBdData);
    }

    if (name === FIELD_TYPES.NLU_RUNTIME) {
      newErrors[FIELD_TYPES.NLU_RUNTIME_MODES] = validateField(FIELD_TYPES.NLU_RUNTIME_MODES, newBdData[FIELD_TYPES.NLU_RUNTIME_MODES], newBdData);
    }

    return newErrors;
  };

  const handleSaveButtonClicked = async () => {
    // Validate configId and assistantId mutual requirement before submit
    const hasConfigId = bdData[FIELD_TYPES.CONFIG_ID] && bdData[FIELD_TYPES.CONFIG_ID].trim() !== "";
    const hasAssistantId = bdData[FIELD_TYPES.ASSISTANT_ID] && bdData[FIELD_TYPES.ASSISTANT_ID].trim() !== "";

    if (hasConfigId && !hasAssistantId) {
      setErrors(prev => ({
        ...prev,
        [FIELD_TYPES.ASSISTANT_ID]: ERROR_MESSAGES.AGENT_REQUIRED_WITH_CONFIG
      }));
      return;
    }

    if (hasAssistantId && !hasConfigId) {
      setErrors(prev => ({
        ...prev,
        [FIELD_TYPES.CONFIG_ID]: ERROR_MESSAGES.CONFIG_REQUIRED_WITH_AGENT
      }));
      return;
    }
    setIsLoading(true);
    await onSubmit(bdData, type);
    setIsLoading(false);
  };

  const handleChange = (e) => {
    const { value, name } = e.target;
    let formattedValue = value;

    if (name === FIELD_TYPES.RELEASE_NAME) {
      formattedValue = formatAndValidateString(value).formattedString;
    }

    if (name === FIELD_TYPES.MEMORY) {
      if (typeof value === "string" && value.trim() === "") {
        const newBdData = { ...bdData };
        delete newBdData[name];
        setBdData(newBdData);

        const newErrors = { ...errors };
        delete newErrors[name];
        setErrors(newErrors);
        return;
      }

      const numValue = Number(value);
      const constraints = NUMERIC_FIELD_CONSTRAINTS.MEMORY;
      if (!isNaN(numValue) && numValue >= constraints.min && numValue <= constraints.max) {
        formattedValue = numValue;
      }
    }

    const newBdData = {
      ...bdData,
      [name]: formattedValue
    };

    // Auto-set NLU runtime mode to "dedicated" if configId or assistantId is added
    if ((name === FIELD_TYPES.CONFIG_ID || name === FIELD_TYPES.ASSISTANT_ID) &&
        formattedValue && formattedValue.trim() !== "") {
      newBdData[FIELD_TYPES.NLU_RUNTIME_MODES] = ["dedicated"];
    }

    setBdData(newBdData);
    if (type === DIALOG_TYPES.create.id) {
      const isConfigIdFeatureFlagOn = getFlag("deploy-branch-deployer-with-config-id");
      const keysToFilter = isConfigIdFeatureFlagOn ? KEYS_TO_FILTER_WITH_CONFIG_ID : KEYS_TO_FILTER;
      const filteredData = filterObjectByKeys(newBdData, keysToFilter);
      localStorage.setItem(BRANCH_DEPLOYER_STORAGE_KEY, JSON.stringify(filteredData));
    }

    let newErrors = {
      ...errors,
      [name]: validateField(name, formattedValue, newBdData)
    };

    newErrors = updateRelatedErrors(name, newBdData, newErrors);
    setErrors(newErrors);
  };

  const isSaveDisabled =
    dashboardSettings?.disableBd ||
    type === DIALOG_TYPES.create.id && (Object.values(errors).some(error => error) || !bdData[FIELD_TYPES.RELEASE_NAME]);

  const renderOptionsField = (field) => (
    <HyroMultiSelect
      value={bdData[field.id]}
      onChange={(newValue) => {
        if (!newValue) return;
        handleChange({
          target: {
            value: newValue?.map(option => option?.display_key || option),
            name: field.id
          }
        });
      }}
      key={field.id}
      options={field.options.map(option => ({
        display_key: option,
        key: option
      }))}
      label={field.label}
      showLabel
      name={field.id}
      disabled={type === DIALOG_TYPES.viewDetails.id}
      error={!!errors?.[field.id]}
      helperText={errors?.[field.id]}
    />
  );

  const renderSwitchField = (field) => (
    <Stack key={field.id} direction="row" alignItems="center">
      <FormControlLabel
        control={
          <Switch
            name={field.id}
            checked={!!bdData[field.id]}
            onChange={(e) => {
              handleChange({
                target: {
                  value: e.target.checked,
                  name: field.id
                }
              });
            }}
            disabled={type === DIALOG_TYPES.viewDetails.id}
          />
        }
        label={
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography>{field.label}</Typography>
            {field.tooltip && (
              <CustomTooltip title={field.tooltip}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <QuestionIcon />
                </div>
              </CustomTooltip>
            )}
          </Stack>
        }
      />
    </Stack>
  );

  const renderTextField = (field) => (
    <TextField
      disabled={type === DIALOG_TYPES.viewDetails.id || type === DIALOG_TYPES.edit.id && field.id === FIELD_TYPES.RELEASE_NAME}
      name={field.id}
      key={field.id}
      label={field.label}
      value={bdData[field.id]}
      onChange={handleChange}
      error={!!errors?.[field.id]}
      helperText={errors?.[field.id]}
      type={field.id === FIELD_TYPES.MEMORY ? "number" : "text"}
      inputProps={
        field.id === FIELD_TYPES.MEMORY
          ? { min: NUMERIC_FIELD_CONSTRAINTS.MEMORY.min, max: NUMERIC_FIELD_CONSTRAINTS.MEMORY.max }
          : {}
      }
    />
  );

  return <>
    <Dialog
      open={open}
      title={dialogContent.title}
      onClose={onClose}
    >
      <DialogTitle>{dialogContent?.title}</DialogTitle>
      <DialogContent>
        <Typography
          style={{ whiteSpace: "pre-line" }}
          variant="body1"
          color="text.800"
        >
          {dialogContent?.subtitle}
        </Typography>
        {type === DIALOG_TYPES.viewDetails.id ?
          <Stack gap={2} my={3}>
            {dialogContent.data?.map((field) => (
              bdData[field.id] && (
                <Typography key={field.id} variant="body1" color="textPrimary">
                  <FieldLabel>{field.label}:</FieldLabel>
                  {field.label.toLowerCase().endsWith("url") ? (
                    <ClickableLink href={bdData[field.id]} target="_blank" rel="noopener noreferrer">
                      {bdData[field.id]}
                    </ClickableLink>
                  ) : (
                    typeof bdData[field.id] === "boolean" ? String(bdData[field.id]) : bdData[field.id]
                  )}
                </Typography>
              )
            ))}
          </Stack>
          :
          <Stack gap={2} my={3}>
            {dialogContent.data?.map(field =>
              field.options
                ? renderOptionsField(field)
                : field.type === "switch"
                  ? renderSwitchField(field)
                  : renderTextField(field)
            )}
          </Stack>}
      </DialogContent>
      <DialogActions>
        <HyroButton variant={BUTTON_VARIANTS.TEXT} color={BUTTON_COLORS.SECONDARY} data-testid={`cancel-${type}-bd-button`} onClick={onClose}>Cancel</HyroButton>
        {type !== DIALOG_TYPES.viewDetails.id &&
          <HyroButton
            data-testid={`${type}-bd-button`}
            variant={BUTTON_VARIANTS.CONTAINED}
            disabled={isSaveDisabled}
            onClick={handleSaveButtonClicked}
            loading={isLoading}
          >
            {dialogContent.actionButtonText}
          </HyroButton>}
      </DialogActions>
    </Dialog >
  </>;
};

const FieldLabel = styled("span")(({ theme }) => ({
  fontWeight: "bold",
  marginRight: theme.spacing(1)
}));

const ClickableLink = styled(Link)(({ theme }) => ({
  color: theme.palette.primary[500],
  textDecoration: "none",
  "&:hover": {
    textDecoration: "underline"
  }
}));
export default BranchDeployerDialog;
